import Image from 'next/image';
import { useMemo } from 'react';

import { Text } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';
import { maskAddressValue } from '@/utils/format';

import { Trigger } from '../../domain';

interface Props {
  trigger: Trigger;
}

/**
 * The V3 row parses the pool out of the trigger signature and links it to a block explorer. Neither
 * applies here: the pool id is stored in the context, and it is not an address — no explorer has a
 * page for it, since a V4 pool has no contract of its own.
 */
export const UniswapV4PoolTriggerDetails = ({ trigger }: Props) => {
  const { getChainIconUrl } = useGetChainInfo();

  const poolId = (trigger.context?.pool_id as string | undefined) ?? '';
  const chainId = trigger.context?.chain_id;

  const chainIconUrl = useMemo(
    () => (chainId ? getChainIconUrl(Number(chainId)) || '' : ''),
    [chainId, getChainIconUrl],
  );

  return (
    <div className="tw:flex tw:flex-row tw:gap-6">
      <div className="tw:flex tw:flex-col">
        <Text variant="body-sm" weight="bold" className="tw:mb-1">
          Type
        </Text>
        <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center">
          <Image src="/assets/png/uniswap.png" width={20} height={20} alt="Uniswap v4" />
          <Text variant="body-sm" className="tw:py-0.5">
            Uniswap v4 - Liquidity Pool
          </Text>
        </div>
      </div>

      <div className="tw:flex tw:flex-col">
        <Text variant="body-sm" weight="bold" className="tw:mb-1">
          Pool ID
        </Text>
        <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center">
          {chainIconUrl && <Image src={chainIconUrl} width={12} height={12} alt={`Chain ${String(chainId)}`} />}
          <Text variant="body-sm" className="tw:py-0.5">
            {maskAddressValue(poolId) ?? '-'}
          </Text>
        </div>
      </div>
    </div>
  );
};
